import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import type { MassagerListParams } from '@/services/admin/massagers';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';

const downloadMassagersCsvFn = async (
  site: string,
  params: MassagerListParams
): Promise<void> => {
  const response = await apiClient.get(`/admin/${site}/massagers/csv`, {
    params,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `massager${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Hook to download admin massagers CSV (API + blob download). Handles success/error toast.
 */
export const useDownloadMassagersCsv = () => {
  const toast = useAppToast();
  const mutation = useMutation({
    mutationFn: ({ site, params }: { site: string; params: MassagerListParams }) =>
      downloadMassagersCsvFn(site, params),
    onSuccess: () => {
      toast.success('CSVを出力しました。');
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'CSVの出力に失敗しました。'));
    },
  });

  const downloadCsv = async (site: string, params: MassagerListParams) => {
    await mutation.mutateAsync({ site, params });
  };

  return { downloadCsv, isDownloading: mutation.isPending };
};
